import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API, Auth } from 'aws-amplify';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getEmailAccountStatus } from '../utils';
import { sanitizeExternalHref } from '../utils/safeRedirect';
import './AccountStatus.css';

// Inner component rendered inside <Elements> so Stripe hooks are available.
function CapacityPaymentForm({ quote, onPaid, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [payError, setPayError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !ready) return;
    setPayError(null);
    setPaying(true);
    try {
      const { error: submitErr } = await elements.submit();
      if (submitErr) { setPayError(submitErr.message); return; }
      // OWASP A01: strip query/hash from URL before passing as return_url
      const safeUrl = `${window.location.origin}${window.location.pathname}`;
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: safeUrl },
        redirect: 'if_required',
      });
      if (error) { setPayError(error.message); return; }
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        onPaid(paymentIntent.id);
        return;
      }
      setPayError('Payment did not complete: ' + (paymentIntent && paymentIntent.status));
    } catch (err) {
      setPayError((err && err.message) || 'Payment error');
    } finally {
      setPaying(false);
    }
  };

  const addedUsers = quote.addedUsers || 0;
  const totalDollars = (quote.totalDueCents / 100).toFixed(2);

  return (
    <form onSubmit={handleSubmit}>
      <h4 style={{ marginTop: 0, marginBottom: '12px' }}>Confirm Capacity Change</h4>
      <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' }}>
        <div style={{ fontWeight: 600, marginBottom: '8px' }}>
          {quote.oldMax} → {quote.newMax} max users (+{addedUsers} added)
        </div>
        {quote.deltaTiers && quote.deltaTiers.map((b, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: '#555' }}>
            <span>{b.label}: {b.users} × ${(b.ratePerUserCents / 100).toFixed(2)}/user</span>
            <span>${(b.subtotalCents / 100).toFixed(2)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
          <span>Service cost (new users):</span>
          <strong>${(quote.setupFeeDeltaCents / 100).toFixed(2)}</strong>
        </div>
        {quote.escrowDeltaCents > 0 ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Escrow ceiling top-up:</span>
            <strong>${(quote.escrowDeltaCents / 100).toFixed(2)}</strong>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#888', fontSize: '12px' }}>
            <span>Escrow ceiling:</span>
            <span>No additional deposit required</span>
          </div>
        )}
        <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '8px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '14px' }}>
          <span>Total due now:</span>
          <span>${totalDollars} USD</span>
        </div>
      </div>
      <AddressElement options={{ mode: 'billing' }} />
      <PaymentElement
        onReady={() => setReady(true)}
        options={{
          fields: { billingDetails: { name: 'never', phone: 'never', address: 'never' } },
          wallets: { applePay: 'never', googlePay: 'never' }
        }}
      />
      {payError && <p style={{ color: 'red', marginTop: '8px', fontSize: '13px' }}>{payError}</p>}
      <div className="modify-action-row" style={{ marginTop: '16px' }}>
        <button type="button" className="modify-cancel-button" onClick={onCancel} disabled={paying}>
          Cancel
        </button>
        <button type="submit" className="modify-apply-button" disabled={!stripe || !elements || !ready || paying}>
          {paying ? 'Processing…' : `Pay $${totalDollars} & Confirm`}
        </button>
      </div>
    </form>
  );
}

// Inner component rendered inside <Elements> (SetupIntent clientSecret) so Stripe hooks
// are available. Saves a card without charging it — used by the standalone
// "Update Payment Method" dashboard action (independent of resizing).
function UpdatePaymentMethodForm({ onConfirmed, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [payError, setPayError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !ready) return;
    setPayError(null);
    setSaving(true);
    try {
      const { error: submitErr } = await elements.submit();
      if (submitErr) { setPayError(submitErr.message); return; }
      // OWASP A01: strip query/hash from URL before passing as return_url
      const safeUrl = `${window.location.origin}${window.location.pathname}`;
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: safeUrl },
        redirect: 'if_required',
      });
      if (error) { setPayError(error.message); return; }
      if (setupIntent && setupIntent.status === 'succeeded') {
        onConfirmed(setupIntent.id);
        return;
      }
      setPayError('Setup did not complete: ' + (setupIntent && setupIntent.status));
    } catch (err) {
      setPayError((err && err.message) || 'Payment error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        onReady={() => setReady(true)}
        options={{
          fields: { billingDetails: { name: 'auto', phone: 'auto', address: 'auto' } },
          wallets: { applePay: 'never', googlePay: 'never' }
        }}
      />
      {payError && <p style={{ color: 'red', marginTop: '8px', fontSize: '13px' }}>{payError}</p>}
      <div className="modify-action-row" style={{ marginTop: '16px' }}>
        <button type="button" className="modify-cancel-button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="modify-apply-button" disabled={!stripe || !elements || !ready || saving}>
          {saving ? 'Saving…' : 'Save Card'}
        </button>
      </div>
    </form>
  );
}

/**
 * AccountStatus Component - Account Activity Details Page
 * 
 * This component shows detailed activity status for a SPECIFIC account (both creation and closure flows).
 * Accessed via direct link from account list or after clicking on a specific account.
 * 
 * Route: /status/:accountEmail/:companyName
 * 
 * Status Tracking: Uses request_status field which tracks the ENTIRE lifecycle:
 * - Account Creation Flow: PENDING → APPROVED → CREATING_ACCOUNT → ACCOUNT_CREATED → 
 *                          DEPLOYING_INFRASTRUCTURE → INFRASTRUCTURE_DEPLOYED → 
 *                          CREATING_DASHBOARD → DASHBOARD_DEPLOYED → COMPLETED
 * - Account Closure Flow: CLOSURE_PENDING → DELETING_DASHBOARD → DELETING_INFRASTRUCTURE → ACCOUNT_CLOSED
 * 
 * NOTE: Account Requests page shows ONLY creation requests (filters out closure statuses).
 *       This page shows ALL statuses for the specific account being viewed.
 * 
 * Flow Detection: Automatically switches UI between creation and closure based on request_status values
 * Status Source: request_status field from Step Functions (with fallback to status for backward compatibility)
 */
const AccountStatus = ({ loggedInUser, onSignOut }) => {
  const { accountEmail, companyName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [accountData, setAccountData] = useState(null);
  const [newestAccount, setNewestAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState(null);
  const pollingIntervalRef = useRef(null);
  // Once a terminal status is observed, polling becomes a no-op so the
  // closed/completed dashboard stays static instead of refetching forever.
  const stoppedRef = useRef(false);

  // Modify-users panel state
  const [modifyOpen, setModifyOpen] = useState(false);
  const [modifyMin, setModifyMin] = useState('');
  const [modifyMax, setModifyMax] = useState('');
  const [modifySavePaymentMethod, setModifySavePaymentMethod] = useState(true);
  const [modifyLoading, setModifyLoading] = useState(false);
  const [modifyError, setModifyError] = useState(null);
  const [modifySuccess, setModifySuccess] = useState(null);
  // Payment step state (scale-up only)
  const [modifyStep, setModifyStep] = useState('form'); // 'form' | 'paying'
  const [modifyQuote, setModifyQuote] = useState(null);
  const [modifyStripePromise, setModifyStripePromise] = useState(null);

  // Update Payment Method panel state (standalone, independent of resizing)
  const [paymentUpdateOpen, setPaymentUpdateOpen] = useState(false);
  const [paymentUpdateLoading, setPaymentUpdateLoading] = useState(false);
  const [paymentUpdateError, setPaymentUpdateError] = useState(null);
  const [paymentUpdateSuccess, setPaymentUpdateSuccess] = useState(null);
  const [paymentUpdateClientSecret, setPaymentUpdateClientSecret] = useState(null);
  const [paymentUpdateStripePromise, setPaymentUpdateStripePromise] = useState(null);

  const recordIdentity = (rec) =>
    rec
      ? `${rec.id || ''}|${rec.request_status || rec.status || ''}|${rec.updated_at || ''}`
      : '';

  const handleBackToDashboard = useCallback(() => {
    const rawStatus = accountData?.request_status || accountData?.status;
    const normalizedStatus = (rawStatus || '').toUpperCase();
    
    // Check if the current account is in a rejected state
    const isRejectedCheck = (status) => {
      const normalized = String(status || '').toUpperCase();
      return normalized === 'REJECTED' || normalized === 'ACCOUNT_CREATION_REJECTED';
    };

    if (isRejectedCheck(normalizedStatus)) {
      navigate('/rejection', { 
        replace: true,
        state: { 
          rejectedAccount: accountData,
          fromStatusPage: true 
        }
      });
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate, accountData]);

  // Step 1: validate inputs, call /modify-capacity-quote.
  // For scale-up: transitions to the payment step.
  // For scale-down or no net change: applies directly.
  const handleReviewCapacity = useCallback(async () => {
    setModifyError(null);
    setModifySuccess(null);
    const min = parseInt(modifyMin, 10);
    const max = parseInt(modifyMax, 10);
    if (isNaN(min) || min < 1 || min > 100) {
      setModifyError('Min users must be between 1 and 100.');
      return;
    }
    if (isNaN(max) || max < 1 || max > 100) {
      setModifyError('Max users must be between 1 and 100. For more than 100 users, please contact support at 800-985-1351.');
      return;
    }
    if (max < min) {
      setModifyError('Max users must be >= min users.');
      return;
    }
    setModifyLoading(true);
    try {
      let token;
      try {
        const session = await Auth.currentSession();
        token = session.getIdToken().getJwtToken();
      } catch (e) { setModifyError('Session expired — please refresh the page and try again.'); setModifyLoading(false); return; }

      const quote = await API.post('accountAppBackend', '/modify-capacity-quote', {
        body: { company_name: accountData.company_name, fleet_max_capacity: max, save_payment_method: modifySavePaymentMethod },
        ...(token && { headers: { Authorization: `Bearer ${token}` } })
      });

      // Diagnostic: log quote response so browser DevTools Console shows the result
      const currentRecordedMax = parseInt(accountData.fleet_max_capacity, 10) || 0;
      console.log('[capacity-diag] min=%o max=%o currentRecordedMax=%o company=%o', min, max, currentRecordedMax, accountData.company_name);
      console.log('[capacity-diag] quote:', JSON.stringify(quote));

      // Guard: the quote can only waive payment when the new max is NOT higher than
      // the current recorded max.  If the server says no-payment but the user
      // explicitly entered a larger number, the Lambda's oldMax must be stale or
      // the account record is missing fleet_max_capacity — surface a clear error
      // rather than letting the modify call hit the payment gate and return 402.
      if (!quote.requiresPayment && max > currentRecordedMax) {
        setModifyError(
          `Unexpected: server waived payment for a scale-up ` +
          `(dashboard shows ${currentRecordedMax}, requested ${max}, ` +
          `server oldMax=${quote.oldMax != null ? quote.oldMax : 'unknown'}). ` +
          `Refreshing the page and retrying may resolve this.`
        );
        return;
      }

      if (!quote.requiresPayment) {
        // Scale-down or no-cost change — apply directly
        await API.post('accountAppBackend', '/modify-capacity', {
          body: { company_name: accountData.company_name, fleet_min_capacity: min, fleet_max_capacity: max },
          ...(token && { headers: { Authorization: `Bearer ${token}` } })
        });
        fetchAccountStatusRef.current();
        setModifySuccess(`Fleet updated to ${min}–${max} users. Changes take effect within a few minutes.`);
        setModifyOpen(false);
        setModifyStep('form');
      } else {
        // Scale-up — show payment form
        setModifyQuote({ ...quote, fleet_min_capacity: min });
        const stripeKey = quote.publishableKey;
        if (!stripeKey || !stripeKey.startsWith('pk_')) {
          setModifyError('Payment configuration error: missing Stripe publishable key. Check /stripe/credentials in Secrets Manager (pk_live or pk_test).');
          return;
        }
        // advancedFraudSignals: false — same iframe-nesting rationale as
        // src/stripe/stripeClient.js.
        setModifyStripePromise(loadStripe(stripeKey, { advancedFraudSignals: false }));
        setModifyStep('paying');
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to load pricing. Please try again.';
      setModifyError(msg);
    } finally {
      setModifyLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifyMin, modifyMax, modifySavePaymentMethod, accountData]);

  // Step 2 (scale-up only): payment confirmed — apply the fleet change.
  const handleCapacityPaid = useCallback(async (paymentIntentId) => {
    setModifyError(null);
    setModifyLoading(true);
    try {
      let token;
      try {
        const session = await Auth.currentSession();
        token = session.getIdToken().getJwtToken();
      } catch (e) { setModifyError('Session expired — please refresh the page and try again.'); setModifyLoading(false); return; }

      const min = modifyQuote.fleet_min_capacity;
      const max = modifyQuote.newMax;
      await API.post('accountAppBackend', '/modify-capacity', {
        body: {
          company_name:          accountData.company_name,
          fleet_min_capacity:    min,
          fleet_max_capacity:    max,
          stripe_payment_intent_id: paymentIntentId
        },
        ...(token && { headers: { Authorization: `Bearer ${token}` } })
      });
      fetchAccountStatusRef.current();
      setModifySuccess(`Fleet updated to ${min}–${max} users. Changes take effect within a few minutes.`);
      setModifyOpen(false);
      setModifyStep('form');
      setModifyQuote(null);
      setModifyStripePromise(null);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to apply fleet change.';
      setModifyError(msg);
      // Reset to form so the user gets a fresh quote+PaymentIntent on retry.
      // Staying on the paying step would reuse the already-confirmed PI → Stripe 400.
      setModifyStep('form');
      setModifyQuote(null);
      setModifyStripePromise(null);
    } finally {
      setModifyLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifyQuote, accountData]);

  // Update Payment Method, step 1: create a SetupIntent and open the card form.
  const handleStartPaymentMethodUpdate = useCallback(async () => {
    setPaymentUpdateError(null);
    setPaymentUpdateSuccess(null);
    setPaymentUpdateLoading(true);
    try {
      let token;
      try {
        const session = await Auth.currentSession();
        token = session.getIdToken().getJwtToken();
      } catch (e) { setPaymentUpdateError('Session expired — please refresh the page and try again.'); setPaymentUpdateLoading(false); return; }

      const resp = await API.post('accountAppBackend', '/update-payment-method-setup-intent', {
        body: { company_name: accountData.company_name },
        ...(token && { headers: { Authorization: `Bearer ${token}` } })
      });
      const stripeKey = resp.publishableKey;
      if (!stripeKey || !stripeKey.startsWith('pk_')) {
        setPaymentUpdateError('Payment configuration error: missing Stripe publishable key.');
        return;
      }
      setPaymentUpdateClientSecret(resp.clientSecret);
      // advancedFraudSignals: false — same iframe-nesting rationale as src/stripe/stripeClient.js.
      setPaymentUpdateStripePromise(loadStripe(stripeKey, { advancedFraudSignals: false }));
      setPaymentUpdateOpen(true);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to start payment method update.';
      setPaymentUpdateError(msg);
    } finally {
      setPaymentUpdateLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData]);

  // Update Payment Method, step 2: SetupIntent confirmed client-side — persist the new
  // payment_method_id server-side (server re-verifies the SetupIntent, never trusts the client).
  const handlePaymentMethodConfirmed = useCallback(async (setupIntentId) => {
    setPaymentUpdateError(null);
    setPaymentUpdateLoading(true);
    try {
      let token;
      try {
        const session = await Auth.currentSession();
        token = session.getIdToken().getJwtToken();
      } catch (e) { setPaymentUpdateError('Session expired — please refresh the page and try again.'); setPaymentUpdateLoading(false); return; }

      const confirmResp = await API.post('accountAppBackend', '/confirm-payment-method-update', {
        body: { company_name: accountData.company_name, setup_intent_id: setupIntentId },
        ...(token && { headers: { Authorization: `Bearer ${token}` } })
      });
      // A suspended account is charged right away with the new card; say what happened.
      const recovery = confirmResp && confirmResp.recovery;
      if (recovery && recovery.outcome === 'RESTORED') {
        setPaymentUpdateSuccess('Payment method updated and the payment succeeded — workstation access is restored.');
      } else if (recovery && recovery.outcome === 'DECLINED') {
        const reason = (recovery.error && recovery.error.message) || 'the charge was declined';
        setPaymentUpdateError(`Payment method saved, but the payment did not go through (${reason}). Workstation access is still suspended — please try another card.`);
      } else if (recovery && recovery.outcome !== 'NOT_SUSPENDED') {
        setPaymentUpdateError('Payment method saved. We could not complete the payment that restores workstation access right now — please contact support.');
      } else {
        setPaymentUpdateSuccess('Payment method updated successfully.');
      }
      setPaymentUpdateOpen(false);
      setPaymentUpdateClientSecret(null);
      setPaymentUpdateStripePromise(null);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to confirm payment method update.';
      setPaymentUpdateError(msg);
    } finally {
      setPaymentUpdateLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData]);

  const activityTypeHint = useMemo(() => {
    const stateHint = location.state && location.state.activityType;
    if (stateHint) {
      const normalized = String(stateHint).toLowerCase();
      try {
        sessionStorage.setItem('activityTypeHint', normalized);
      } catch (e) { /* ignore storage errors */ }
      return normalized;
    }
    try {
      const stored = sessionStorage.getItem('activityTypeHint');
      return stored ? stored.toLowerCase() : null;
    } catch (e) {
      return null;
    }
  }, [location.state]);

  const initialRejectedAccount = useMemo(
    () => location.state?.rejectedAccount || null,
    [location.state]
  );

  const decodedAccountEmail = useMemo(() => {
    try {
      return decodeURIComponent(accountEmail || '');
    } catch (err) {
      console.error('Failed to decode account email param', err);
      return accountEmail || '';
    }
  }, [accountEmail]);

  const decodedCompanyName = useMemo(() => {
    try {
      return decodeURIComponent(companyName || '');
    } catch (err) {
      console.error('Failed to decode company name param', err);
      return companyName || '';
    }
  }, [companyName]);

  const normalizedAccountEmail = useMemo(
    () => decodedAccountEmail.trim().toLowerCase(),
    [decodedAccountEmail]
  );

  const normalizedCompanyName = useMemo(
    () => decodedCompanyName.trim().toLowerCase(),
    [decodedCompanyName]
  );

  const normalizeCompanyKey = useCallback((value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }, []);

  const isTerminalState = useMemo(() => {
    if (!accountData) return false;
    const normalizedStatus = (accountData.request_status || accountData.status || '').toUpperCase();
    
    // We can't call getStageInfo here because it's defined later, but we can 
    // check terminal status directly as documented in the flow detection notes.
    const terminalStatuses = [
      'COMPLETED', 'REJECTED', 'ACCOUNT_CREATION_REJECTED', 'ACCOUNT_CLOSED',
      'FAILED', 'TIMED_OUT', 'PAYMENT_FAILED', 'DUPLICATE_REQUEST',
      'FAILED_IMAGE_SHARING', 'FAILED_INFRASTRUCTURE_DEPLOYMENT', 'FAILED_DASHBOARD_DEPLOYMENT',
      'DASHBOARD_DELETION_FAILED', 'INFRASTRUCTURE_DELETION_FAILED',
      'PENDING_SETTLEMENT', 'ARCHIVE_MIGRATION_FAILED', 'CLOSURE_VALIDATION_FAILED',
      'STACKSET_DELETION_FAILED', 'IMAGE_PERMISSION_DELETION_FAILED', 'CLOSURE_FAILED'
    ];
    return terminalStatuses.includes(normalizedStatus);
  }, [accountData]);

  const isViewingOlderRequest = useMemo(() => {
    if (!accountData || !newestAccount) return false;
    
    if (accountData.id && newestAccount.id) {
      return accountData.id !== newestAccount.id;
    }
    
    const currentKey = `${accountData.account_email}-${accountData.company_name}`;
    const newestKey = `${newestAccount.account_email}-${newestAccount.company_name}`;
    
    return currentKey !== newestKey;
  }, [accountData, newestAccount]);

  const handleSwitchToNewest = useCallback(() => {
    if (!newestAccount) return;
    const email = encodeURIComponent(newestAccount.account_email);
    const company = encodeURIComponent(newestAccount.company_name);
    navigate(`/status/${email}/${company}`);
  }, [newestAccount, navigate]);

  const parseTimestamp = useCallback((value) => {
    if (!value) {
      return 0;
    }
    const time = Date.parse(value);
    return Number.isNaN(time) ? 0 : time;
  }, []);

  const getStatusRank = useCallback((status) => {
    const normalizedStatus = (status || '').toUpperCase();

    const creationOrder = [
      'PENDING',
      'PENDING_APPROVAL',
      'APPROVED',
      'CREATING_ACCOUNT',
      'ACCOUNT_CREATED',
      'DEPLOYING_INFRASTRUCTURE',
      'INFRASTRUCTURE_DEPLOYED',
      'CREATING_DASHBOARD',
      'DASHBOARD_DEPLOYED',
      'COMPLETED'
    ];

    const closureOrder = [
      'CLOSURE_PENDING',
      'DELETING_DASHBOARD',
      'DELETING_INFRASTRUCTURE',
      'ACCOUNT_CLOSED',
      'PENDING_SETTLEMENT'
    ];

    const terminalOrder = [
      'REJECTED',
      'ACCOUNT_CREATION_REJECTED',
      'TIMED_OUT',
      'FAILED',
      'FAILED_IMAGE_SHARING',
      'FAILED_INFRASTRUCTURE_DEPLOYMENT',
      'FAILED_DASHBOARD_DEPLOYMENT',
      'INFRASTRUCTURE_CREATION_FAILED',
      'DASHBOARD_CREATION_FAILED',
      'DASHBOARD_DELETION_FAILED',
      'INFRASTRUCTURE_DELETION_FAILED',
      'IMAGE_PERMISSION_DELETION_FAILED',
      'STACKSET_DELETION_FAILED',
      'CLOSURE_FAILED',
      'CLOSURE_VALIDATION_FAILED'
    ];

    const creationIndex = creationOrder.indexOf(normalizedStatus);
    if (creationIndex !== -1) {
      return creationIndex + 1;
    }

    const closureIndex = closureOrder.indexOf(normalizedStatus);
    if (closureIndex !== -1) {
      return closureIndex + 1;
    }

    const terminalIndex = terminalOrder.indexOf(normalizedStatus);
    if (terminalIndex !== -1) {
      // Ensure terminal outcomes outrank in-flight statuses when timestamps tie
      return 100 + terminalIndex;
    }

    return 0;
  }, []);

  const getRecordTime = useCallback((record) => {
    return parseTimestamp(record?.updated_at) ||
      parseTimestamp(record?.rejected_at) ||
      parseTimestamp(record?.approved_at) ||
      parseTimestamp(record?.created_at);
  }, [parseTimestamp]);

  const isRejectedStatus = useCallback((status) => {
    const normalizedStatus = String(status || '').toUpperCase();
    return normalizedStatus === 'REJECTED' || normalizedStatus === 'ACCOUNT_CREATION_REJECTED';
  }, []);

  // Stabilize loggedInUser dependency by using email as key
  const loggedInUserEmail = loggedInUser?.attributes?.email;

  const fetchAccountStatus = useCallback(async () => {
    try {
      // Only show loading indicator on initial fetch when we have no existing data
      setLoading((prev) => {
        // If we're already loading (initial load), keep it true.
        // If fetching via interval update (prev=false), keep it false to avoid UI flash.
        return prev;
      });
      // Better: use a ref for hasLoaded

      const accountList = await getEmailAccountStatus(loggedInUser);

      // Handle null response (API error) separately from empty list
      if (accountList === null) {
        // If we have previous successful data, keep it and just stop loading
        if (accountData && !accountData.preliminary) {
          setLoading(false);
          return;
        }
        // If we don't have data, we might show an error or fallback, 
        // but let's be careful not to flash preliminary state
      }

      // Find all matching records and select the most relevant/latest one.
      // Some environments can return duplicate historical rows for the same
      // account/company pair. Choosing the first row can cause status regressions
      // (e.g. jumping back from dashboard deployment to account creation).
      const targetCompanyKey = normalizeCompanyKey(normalizedCompanyName);
      const userSub = loggedInUser?.attributes?.sub;

      const emailMatchedAccounts = (accountList || []).filter((acc) => {
        const accountEmailValue = (acc.account_email || '').trim().toLowerCase();
        const requesterEmailValue = (acc.requester_email || '').trim().toLowerCase();
        const emailMatches = accountEmailValue === normalizedAccountEmail || requesterEmailValue === normalizedAccountEmail;

        if (userSub) {
          // STRICT SECURITY: Record MUST belong to the current authenticated Cognito identity.
          // This prevents "Identity Flicker" if multiple users shared the same email history.
          return emailMatches && acc.cognito_sub === userSub;
        }
        return emailMatches;
      });

      const sortRecords = (records) => [...records].sort((a, b) => {
        const bTime = getRecordTime(b);
        const aTime = getRecordTime(a);

        if (Math.abs(bTime - aTime) < 5000) {
          const bRank = getStatusRank(b.request_status || b.status);
          const aRank = getStatusRank(a.request_status || a.status);
          return bRank - aRank;
        }

        return bTime - aTime;
      });

      let matchingAccounts = emailMatchedAccounts.filter(acc => {
        const dbKey = normalizeCompanyKey(acc.company_name);
        const matchesKey = dbKey === targetCompanyKey;

        // Intent Guard: If user just requested an action (creation or closure), we prioritize that flow.
        if (matchesKey && (activityTypeHint === 'creation' || activityTypeHint === 'closure')) {
          const status = (acc.request_status || acc.status || '').toUpperCase();
          const isClosureState = ['CLOSURE_PENDING', 'CLOSING', 'DELETING_DASHBOARD', 'DELETING_INFRASTRUCTURE', 'PENDING_SETTLEMENT', 'CLOSED', 'ACCOUNT_CLOSED'].includes(status);
          
          if (activityTypeHint === 'creation' && status === 'COMPLETED') {
            // Check if this record is actually recent (e.g., updated in the last 2 hours)
            // to avoid showing ancient completed requests while still allowing the current one to finish.
            const recordTime = getRecordTime(acc);
            const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
            if (recordTime < twoHoursAgo) {
              return false;
            }
          }
          if (activityTypeHint === 'closure' && !isClosureState) {
            // During closure, we only want to see state transitions related to the closure.
            // This prevents "Awaiting approver decision" (a creation status) from showing
            // if an older creation request is still in the history.
            return false;
          }
        }

        return matchesKey;
      });

      const account = sortRecords(matchingAccounts)[0];
      const sortedEmailMatches = sortRecords(emailMatchedAccounts);
      const absoluteNewest = sortedEmailMatches[0];

      if (absoluteNewest) {
        setNewestAccount((prev) =>
          recordIdentity(prev) === recordIdentity(absoluteNewest) ? prev : absoluteNewest
        );
      }

      if (account) {
        setAccountData((prevData) => {
          if (!prevData) {
            return account;
          }

          // Bail out when the incoming record is structurally the same as
          // what we already have. Without this, every poll replaces the
          // object reference and forces a re-render even though nothing
          // changed, making terminal-state dashboards look like they are
          // constantly reloading.
          if (recordIdentity(prevData) === recordIdentity(account)) {
            return prevData;
          }

          const prevTime = getRecordTime(prevData);
          const nextTime = getRecordTime(account);

          if (nextTime > prevTime) {
            return account;
          }

          // Use the same fuzzy logic for state updates
          if (Math.abs(nextTime - prevTime) < 5000) {
            const prevRank = getStatusRank(prevData.request_status || prevData.status);
            const nextRank = getStatusRank(account.request_status || account.status);
            if (nextRank >= prevRank) {
              return account;
            }
          }

          return prevData;
        });
        setError(null);

        // Stop polling if account is completed or failed
        const status = (account.request_status || account.status || '').toUpperCase();
        const terminalStatuses = [
          'COMPLETED',
          'FAILED',
          'REJECTED',
          'ACCOUNT_CREATION_REJECTED',
          'TIMED_OUT',
          'PAYMENT_FAILED',
          'DUPLICATE_REQUEST',
          'FAILED_IMAGE_SHARING',
          'FAILED_INFRASTRUCTURE_DEPLOYMENT',
          'FAILED_DASHBOARD_DEPLOYMENT',
          'ACCOUNT_CLOSED',
          'DASHBOARD_DELETION_FAILED',
          'INFRASTRUCTURE_DELETION_FAILED',
          'IMAGE_PERMISSION_DELETION_FAILED',
          'STACKSET_DELETION_FAILED',
          'CLOSURE_FAILED',
          'CLOSURE_VALIDATION_FAILED',
          'ARCHIVE_MIGRATION_FAILED',
          'PENDING_SETTLEMENT'
        ];
        
        if (terminalStatuses.includes(status)) {
          stoppedRef.current = true;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } else {

        // If account not found but we have URL params, show preliminary info
        if (decodedAccountEmail && decodedCompanyName) {
          if (initialRejectedAccount) {
            setAccountData(initialRejectedAccount);
            setError(null);
            return;
          }

          // Always show preliminary data when we have URL params
          setAccountData(prevData => {
            // Keep existing valid data if available
            if (prevData && !prevData.preliminary) {
              return prevData;
            }
            
            return {
              account_email: decodedAccountEmail,
              company_name: decodedCompanyName,
              request_status: 'PENDING_APPROVAL',
              preliminary: true // Flag to indicate this is preliminary data
            };
          });
          setError(null);
        } else {
          setError('Account request not found');
        }
      }
    } catch (err) {
      if (err?.name === 'AuthSessionInvalidError') {
        onSignOut?.();
        return;
      }
      console.error('Error fetching account status:', err);
      if (initialRejectedAccount) {
        setAccountData(initialRejectedAccount);
        setError(null);
        setLoading(false);
        return;
      }

      // On error, if we have URL params, show preliminary info
      if (decodedAccountEmail && decodedCompanyName) {
        setAccountData(prevData => {
          // Keep existing valid data if available
          if (prevData && !prevData.preliminary) {
            return prevData;
          }

          return {
            account_email: decodedAccountEmail,
            company_name: decodedCompanyName,
            request_status: 'PENDING_APPROVAL',
            preliminary: true
          };
        });
        setError(null);
      } else {
        setError('Failed to fetch account status');
      }
    } finally {
      setLoading(false);
      setIsInitializing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    normalizedAccountEmail,
    normalizedCompanyName,
    loggedInUserEmail, // Stable dependency
    loggedInUser,      // Required for getEmailAccountStatus call but we rely on email changing
    decodedAccountEmail,
    decodedCompanyName,
    initialRejectedAccount,
    normalizeCompanyKey,
    getRecordTime,
    getStatusRank
    // accountData // Removed dependency to prevent re-creation loop
  ]);

  // Keep latest fetchAccountStatus in a ref so the polling effect below
  // does NOT depend on it. Previously the polling useEffect listed
  // `fetchAccountStatus` as a dependency, which caused the interval to be
  // torn down and re-created (and an immediate refetch fired) on every
  // render where a dependency of fetchAccountStatus (notably the
  // `loggedInUser` prop reference) changed identity. On terminal-state
  // pages (e.g. ACCOUNT_CLOSED) this manifested as a constantly
  // re-rendering / reloading dashboard.
  const fetchAccountStatusRef = useRef(fetchAccountStatus);
  useEffect(() => {
    fetchAccountStatusRef.current = fetchAccountStatus;
  }, [fetchAccountStatus]);

  // Initial fetch and setup polling — rebuild interval ONLY when the
  // viewed account identity changes (primitive strings).
  useEffect(() => {
    stoppedRef.current = false;
    fetchAccountStatusRef.current();

    // Poll every 10 seconds for status updates
    const interval = setInterval(() => {
      if (stoppedRef.current) return;
      fetchAccountStatusRef.current();
    }, 10000);

    pollingIntervalRef.current = interval;

    // Cleanup on unmount or account change
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [normalizedAccountEmail, normalizedCompanyName]);

  const getStageInfo = (status, activityTypeOverride) => {
    const normalizedStatus = (status || '').toUpperCase();
    const hasClosureHint = activityTypeOverride === 'closure';
    const hasCreationHint = activityTypeOverride === 'creation';
    
    // Detect if this is a closure flow
    const closureStatuses = ['CLOSURE_PENDING', 'DELETING_DASHBOARD', 'DELETING_INFRASTRUCTURE', 'ACCOUNT_CLOSED', 
                 'DASHBOARD_DELETION_FAILED', 'INFRASTRUCTURE_DELETION_FAILED', 'IMAGE_PERMISSION_DELETION_FAILED', 'STACKSET_DELETION_FAILED', 
                 'CLOSURE_FAILED', 'CLOSURE_VALIDATION_FAILED', 'PENDING_SETTLEMENT', 'ARCHIVE_MIGRATION_FAILED'];

    const isClosureFlow = hasClosureHint || (!hasCreationHint && closureStatuses.includes(normalizedStatus));
    
    if (isClosureFlow) {
      // Closure flow stages
      // Stage 1: Deleting Dashboard
      const stage1Complete = normalizedStatus === 'DELETING_INFRASTRUCTURE' || normalizedStatus === 'ACCOUNT_CLOSED' || normalizedStatus === 'PENDING_SETTLEMENT';
      const stage1Active = normalizedStatus === 'DELETING_DASHBOARD' || normalizedStatus === 'CLOSURE_PENDING';
      
      // Stage 2: Deleting Infrastructure
      const stage2Complete = normalizedStatus === 'ACCOUNT_CLOSED' || normalizedStatus === 'PENDING_SETTLEMENT';
      const stage2Active = normalizedStatus === 'DELETING_INFRASTRUCTURE';
      
      return {
        isClosureFlow: true,
        stage1: { complete: stage1Complete, active: stage1Active },
        stage2: { complete: stage2Complete, active: stage2Active },
        stage3: { complete: false, active: false }, // No stage 3 for closure
        isCompleted: normalizedStatus === 'ACCOUNT_CLOSED' || normalizedStatus === 'PENDING_SETTLEMENT',
        isFailed: normalizedStatus === 'DASHBOARD_DELETION_FAILED' || normalizedStatus === 'INFRASTRUCTURE_DELETION_FAILED' || normalizedStatus === 'IMAGE_PERMISSION_DELETION_FAILED' || 
                  normalizedStatus === 'STACKSET_DELETION_FAILED' || normalizedStatus === 'CLOSURE_FAILED' || 
                  normalizedStatus === 'CLOSURE_VALIDATION_FAILED' || normalizedStatus === 'ARCHIVE_MIGRATION_FAILED'
      };
    } else {
      // Creation flow stages
      // Stage 1: Creating Account
      const stage1Statuses = ['PENDING', 'PENDING_APPROVAL', 'APPROVED', 'CREATING_ACCOUNT'];
      const stage1Complete = !stage1Statuses.includes(normalizedStatus) && normalizedStatus !== '';
      const stage1Active = stage1Statuses.includes(normalizedStatus);
      
      // Stage 2: Deploying Infrastructure
      const stage2Statuses = ['ACCOUNT_CREATED', 'DEPLOYING_INFRASTRUCTURE'];
      const stage2Complete = normalizedStatus === 'INFRASTRUCTURE_DEPLOYED' || 
                            normalizedStatus === 'CREATING_DASHBOARD' || 
                            normalizedStatus === 'DASHBOARD_DEPLOYED' || 
                            normalizedStatus === 'COMPLETED';
      const stage2Active = stage2Statuses.includes(normalizedStatus);
      
      // Stage 3: Deploying Dashboard
      const stage3Statuses = ['INFRASTRUCTURE_DEPLOYED', 'CREATING_DASHBOARD', 'DASHBOARD_DEPLOYED'];
      const stage3Complete = normalizedStatus === 'COMPLETED';
      const stage3Active = stage3Statuses.includes(normalizedStatus);
      
      return {
        isClosureFlow: false,
        stage1: { complete: stage1Complete, active: stage1Active },
        stage2: { complete: stage2Complete, active: stage2Active },
        stage3: { complete: stage3Complete, active: stage3Active },
        isCompleted: normalizedStatus === 'COMPLETED',
        isFailed: normalizedStatus.includes('FAILED') || normalizedStatus === 'REJECTED' || normalizedStatus === 'ACCOUNT_CREATION_REJECTED' || normalizedStatus === 'TIMED_OUT'
      };
    }
  };

  const getStatusMessage = (status, hint) => {
    const normalizedStatus = (status || '').toUpperCase();

    // During a closure flow, suppress creation-flow messages that may leak
    // through when the underlying DynamoDB record has not yet transitioned
    // to a closure status (e.g. Lambda validation still running/failing).
    const creationOnlyStatuses = new Set([
      'PENDING', 'PENDING_APPROVAL', 'APPROVED', 'CREATING_ACCOUNT',
      'ACCOUNT_CREATED', 'DEPLOYING_INFRASTRUCTURE', 'INFRASTRUCTURE_DEPLOYED',
      'CREATING_DASHBOARD', 'DASHBOARD_DEPLOYED', 'COMPLETED'
    ]);
    if (hint === 'closure' && creationOnlyStatuses.has(normalizedStatus)) {
      return 'Preparing account closure…';
    }

    const statusMessages = {
      // Account Creation Statuses
      'PENDING': 'Approval request submitted. Waiting for reviewer (automatically expires after 24 hours)...',
      'PENDING_APPROVAL': 'Awaiting approver decision. Email sent to administrator (automatically expires after 24 hours)...',
      'APPROVED': '✅ Request approved! Starting account creation...',
      'CREATING_ACCOUNT': 'Creating AWS account and initial setup...',
      'ACCOUNT_CREATED': '✅ AWS account created successfully! Sharing AppStream images and preparing infrastructure...',
      'DEPLOYING_INFRASTRUCTURE': 'Deploying VPC, security groups, and AppStream fleet...',
      'INFRASTRUCTURE_DEPLOYED': '✅ Infrastructure deployed successfully! Setting up dashboard...',
      'CREATING_DASHBOARD': 'Setting up dashboard and final configurations...',
      'DASHBOARD_DEPLOYED': '✅ Dashboard deployed successfully! Finalizing setup...',
      'COMPLETED': '✅ Account setup completed successfully!',
      'REJECTED': '❌ Request was rejected by administrator.',
      'ACCOUNT_CREATION_REJECTED': '❌ Account creation rejected by administrator.',
      'TIMED_OUT': '⏱️ Request timed out. Please submit a new request.',
      'FAILED': 'Account creation failed. Please contact support.',
      'PAYMENT_FAILED': '❌ Payment processing failed. Please submit a new request with a valid payment method.',
      'DUPLICATE_REQUEST': '❌ A duplicate request was detected and could not be processed. Please submit a new request.',
      'FAILED_IMAGE_SHARING': 'Failed to share AppStream images. Please contact support.',
      'FAILED_INFRASTRUCTURE_DEPLOYMENT': 'Infrastructure deployment failed. Please contact support.',
      'FAILED_DASHBOARD_DEPLOYMENT': 'Dashboard deployment failed. Please contact support.',
      
      // Account Closure Statuses
      'CLOSURE_PENDING': 'Account closure request initiated. Starting deletion process...',
      'DELETING_DASHBOARD': 'Removing dashboard resources (Cognito, CloudFront, AppStream)...',
      'DELETING_INFRASTRUCTURE': 'Removing infrastructure resources (VPC, security groups)...',
      'ACCOUNT_CLOSED': '✅ Account closure completed successfully!',
      'DASHBOARD_DELETION_FAILED': '❌ Dashboard deletion failed. Please retry or contact support.',
      'INFRASTRUCTURE_DELETION_FAILED': '❌ Infrastructure deletion failed. Please retry or contact support.',
      'IMAGE_PERMISSION_DELETION_FAILED': '❌ Failed to remove AppStream image permissions. Deploy delete-appstream-image-permissions Lambda and retry closure.',
      'STACKSET_DELETION_FAILED': '❌ StackSet deletion failed. Template cleanup incomplete. Please retry or contact support.',
      'CLOSURE_FAILED': '❌ Account closure failed. Please retry or contact support.',
      'CLOSURE_VALIDATION_FAILED': '❌ Closure validation failed. Account may not be eligible for closure.',
      'PENDING_SETTLEMENT': '✅ Account closed successfully. Finalizing billing and settlement...',
      'ARCHIVE_MIGRATION_FAILED': '❌ Account closure aborted: Data archive process failed.'
    };
    
    return statusMessages[normalizedStatus] || `Status: ${status}`;
  };

  // NOTE: A previous version auto-navigated to "/" 5 seconds after reaching
  // COMPLETED / ACCOUNT_CLOSED / PENDING_SETTLEMENT. That made sense when
  // the archive download lived on a separate page; now the closed-account
  // dashboard hosts both the archive download and the rest of the account
  // information, so the redirect was bouncing users off this page in a
  // loop. The auto-redirect has been removed intentionally - the user
  // stays on this page until they navigate away themselves.

  if (isInitializing) {
    return (
      <div className="status-container">
        <div className="status-card loading-transition">
          <div className="loading-spinner"></div>
          <h2>Synchronizing Activity...</h2>
          <p>Connecting to secure backend to retrieve the most recent deployment status for <strong>{decodedCompanyName}</strong>.</p>
          <div className="status-message">
            <div className="pulse-dot"></div>
            <span>Please wait while we finalize the connection...</span>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !accountData) {
    return (
      <div className="status-container">
        <div className="status-card">
          <div className="loading-spinner"></div>
          <p>Loading account status...</p>
        </div>
      </div>
    );
  }

  if (error || !accountData) {
    const shouldReturnToRejectedDashboard = Boolean(
      initialRejectedAccount ||
      location.state?.returnToRejectedDashboard ||
      isRejectedStatus(location.state?.accountData?.request_status || location.state?.accountData?.status)
    );

    return (
      <div className="status-container">
        <div className="status-card error">
          <h2>❌ Error</h2>
          <p>{error || 'Account not found'}</p>
          <button onClick={handleBackToDashboard} className="back-button">
            {shouldReturnToRejectedDashboard ? 'Back to Rejected Dashboard' : 'Back to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  const rawStatus = accountData.request_status || accountData.status;
  const normalizedStatus = (rawStatus || '').toUpperCase();
  const stageInfo = getStageInfo(rawStatus, activityTypeHint);
  const statusMessage = getStatusMessage(rawStatus, activityTypeHint);
  const backDashboardLabel = isRejectedStatus(normalizedStatus)
    ? 'Back to Rejected Dashboard'
    : 'Back to Dashboard';

  const toExternalUrl = (value) => {
    const url = String(value || '').trim();
    if (!url) return '';

    // Same-origin path: keep as-is (safe).
    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }

    // mailto:, tel:, etc. — non-http(s) schemes are not navigation targets.
    if (/^[a-z][a-z0-9+.-]*:/i.test(url) && !/^https?:/i.test(url)) {
      return url;
    }

    // Build a candidate https URL, then run it through the allowlist.
    let candidate = url;
    if (candidate.startsWith('//')) {
      candidate = `https:${candidate}`;
    } else if (!/^https?:\/\//i.test(candidate)) {
      candidate = `https://${candidate}`;
    }

    // OWASP A01: drop anything not on the allowlist so it cannot be navigated to.
    return sanitizeExternalHref(candidate);
  };

  const adminSignInUrl = accountData.sign_in_url || accountData.dashboard_url || accountData.website_url;
  const adminSignInUrlWithPath = adminSignInUrl ? (adminSignInUrl.endsWith('/dashboard.html') ? adminSignInUrl : `${adminSignInUrl}/dashboard.html`) : '';
  const userPortalUrl = accountData.user_portal_url || accountData.dashboard_url || accountData.website_url;
  const signUpUrl = accountData.sign_up_url || '';
  const workstationUrl = accountData.appstream_url || userPortalUrl || adminSignInUrl;

  const adminSignInHref = toExternalUrl(adminSignInUrlWithPath);
  const signUpHref = toExternalUrl(signUpUrl);
  const workstationHref = toExternalUrl(workstationUrl);

  const terminalNote = accountData.failure_reason || accountData.approval_reason;

  // ---- Closure data-export archive (written by Step Function: UpdateDashboardWithArchiveURL) ----
  const formatBytes = (bytes) => {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  };
  const archiveUrl = accountData.archive_download_url;
  const archiveStatus = String(accountData.archive_status || '').toUpperCase();
  const archiveObjects = accountData.archive_objects;
  const archiveSizeBytes = accountData.archive_size_bytes;
  const archiveCreatedAt = accountData.archive_created_at;
  const hasArchiveData = Boolean(archiveUrl || archiveStatus);
  const hasUsableArchive = archiveStatus === 'SUCCESS' && archiveUrl && archiveUrl !== 'N/A';
  const archivePanel = hasArchiveData ? (
    <div className="archive-download-section">
      <h3>Data Export Archive</h3>
      {hasUsableArchive ? (
        <div className="url-card archive-card">
          <div className="url-card-header">
            <span className="url-icon">📦</span>
            <h4>Download Your Data Archive</h4>
          </div>
          <p className="archive-meta">
            {archiveObjects && <span>{archiveObjects} file(s)</span>}
            {archiveSizeBytes && formatBytes(archiveSizeBytes) && (
              <span> &middot; {formatBytes(archiveSizeBytes)}</span>
            )}
            {archiveCreatedAt && (
              <span> &middot; created {new Date(archiveCreatedAt).toLocaleString()}</span>
            )}
          </p>
          <a
            href={archiveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="url-link archive-download-button"
          >
            ⬇ Download Archive
          </a>
          <p className="archive-note">
            This is a temporary pre-signed link. Please download promptly &mdash; it will expire.
          </p>
        </div>
      ) : (
        <div className="archive-failure-notice">
          <span>⚠️ Data export archive was not generated for this closure.</span>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="status-container">
      <div className={`status-card ${isTerminalState ? 'terminal-card' : ''}`}>
        {isViewingOlderRequest && (
          <div className="switch-request-banner">
            <span>You are viewing an older request. </span>
            <button onClick={handleSwitchToNewest} className="switch-link-button">
              [Switch to Newest Request]
            </button>
          </div>
        )}
        <h1>Account Activity Status</h1>
        
        <div className="account-info">
          <div className="info-row">
            <span className="info-label">Company:</span>
            <span className="info-value">{accountData.company_name}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Email:</span>
            <span className="info-value">{accountData.account_email}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Name Prefix:</span>
            <span className="info-value">{accountData.name_prefix}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Activity Type:</span>
            <span className="info-value">{stageInfo.isClosureFlow ? 'Account Closure' : 'Account Deployment'}</span>
          </div>
          {isTerminalState && terminalNote && (
            <div className="info-row terminal-note-section">
              <span className="info-label">Note:</span>
              <span className="info-value">{terminalNote}</span>
            </div>
          )}
        </div>

        {/* Access links removed per UI update */}

        {/* Status Message Banner */}
        {!isTerminalState && normalizedStatus === 'APPROVED' && (
          <div className="status-message success-message">
            <span>✅ {statusMessage}</span>
            {accountData.approval_reason && (
              <p className="success-details">Reason: {accountData.approval_reason}</p>
            )}
            {accountData.approved_at && (
              <p className="approval-time">Approved at: {new Date(accountData.approved_at).toLocaleString()}</p>
            )}
          </div>
        )}

        {!isTerminalState && (normalizedStatus === 'REJECTED' || normalizedStatus === 'ACCOUNT_CREATION_REJECTED') && (
          <div className="status-message error-message">
            <span>❌ {statusMessage}</span>
            {accountData.approval_reason && (
              <p className="error-details">Reason: {accountData.approval_reason}</p>
            )}
            {accountData.rejected_at && (
              <p className="rejection-time">Rejected at: {new Date(accountData.rejected_at).toLocaleString()}</p>
            )}
          </div>
        )}

        {!isTerminalState && !stageInfo.isCompleted && !stageInfo.isFailed && normalizedStatus !== 'APPROVED' && normalizedStatus !== 'REJECTED' && normalizedStatus !== 'ACCOUNT_CREATION_REJECTED' && (
          <div className="status-message">
            <div className="pulse-dot"></div>
            <span>
              {statusMessage}
              {normalizedStatus === 'PENDING_APPROVAL' && accountData.approver_email && (
                <>
                  {' '}
                  Awaiting response from <strong>{accountData.approver_email}</strong>.
                </>
              )}
            </span>
          </div>
        )}

        {!isTerminalState && stageInfo.isFailed && normalizedStatus !== 'REJECTED' && normalizedStatus !== 'ACCOUNT_CREATION_REJECTED' && (
          <div className="status-message error-message">
            <span>❌ {statusMessage}</span>
            {accountData.failure_reason && (
              <p className="error-details">{accountData.failure_reason}</p>
            )}
            {accountData.approval_reason && (
              <p className="error-details">{accountData.approval_reason}</p>
            )}
          </div>
        )}

        {/* Progress Stages */}
        {!isTerminalState && !stageInfo.isCompleted && (
          <div className="stages-container">
            {stageInfo.isClosureFlow ? (
              // Closure Flow Stages
              <>
                {/* Stage 1: Deleting Dashboard */}
                <div className={`stage ${stageInfo.stage1.complete ? 'complete' : stageInfo.stage1.active ? 'active' : ''}`}>
                  <div className="stage-icon">
                    {stageInfo.stage1.complete ? '✓' : stageInfo.stage1.active ? '⟳' : '1'}
                  </div>
                  <div className="stage-content">
                    <h3>Deleting Dashboard</h3>
                    <p>Removing Cognito, CloudFront, and AppStream stack</p>
                  </div>
                </div>

                <div className={`stage-connector ${stageInfo.stage1.complete ? 'complete' : ''}`}></div>

                {/* Stage 2: Deleting Infrastructure */}
                <div className={`stage ${stageInfo.stage2.complete ? 'complete' : stageInfo.stage2.active ? 'active' : ''}`}>
                  <div className="stage-icon">
                    {stageInfo.stage2.complete ? '✓' : stageInfo.stage2.active ? '⟳' : '2'}
                  </div>
                  <div className="stage-content">
                    <h3>Deleting Infrastructure</h3>
                    <p>Removing VPC, security groups, and AppStream fleet</p>
                  </div>
                </div>
              </>
            ) : (
              // Creation Flow Stages
              <>
                {/* Stage 1: Creating Account */}
                <div className={`stage ${stageInfo.stage1.complete ? 'complete' : stageInfo.stage1.active ? 'active' : ''}`}>
                  <div className="stage-icon">
                    {stageInfo.stage1.complete ? '✓' : stageInfo.stage1.active ? '⟳' : '1'}
                  </div>
                  <div className="stage-content">
                    <h3>Creating Account</h3>
                    <p>AWS account provisioning and initial setup</p>
                  </div>
                </div>

                <div className={`stage-connector ${stageInfo.stage1.complete ? 'complete' : ''}`}></div>

                {/* Stage 2: Deploying Infrastructure */}
                <div className={`stage ${stageInfo.stage2.complete ? 'complete' : stageInfo.stage2.active ? 'active' : ''}`}>
                  <div className="stage-icon">
                    {stageInfo.stage2.complete ? '✓' : stageInfo.stage2.active ? '⟳' : '2'}
                  </div>
                  <div className="stage-content">
                    <h3>Deploying Infrastructure</h3>
                    <p>VPC, security groups, and AppStream fleet</p>
                  </div>
                </div>

                <div className={`stage-connector ${stageInfo.stage2.complete ? 'complete' : ''}`}></div>

                {/* Stage 3: Deploying Dashboard */}
                <div className={`stage ${stageInfo.stage3.complete ? 'complete' : stageInfo.stage3.active ? 'active' : ''}`}>
                  <div className="stage-icon">
                    {stageInfo.stage3.complete ? '✓' : stageInfo.stage3.active ? '⟳' : '3'}
                  </div>
                  <div className="stage-content">
                    <h3>Deploying Dashboard</h3>
                    <p>Cognito, CloudFront, and AppStream stack</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Dashboard Information */}
        {!isTerminalState && !stageInfo.isCompleted && !stageInfo.isClosureFlow && (
          <div className="dashboard-info-section">
            <h3>Dashboard Information</h3>
            <div className="dashboard-details">
              <div className="dashboard-item">
                <span className="dashboard-label">Account Email:</span>
                <span className="dashboard-value">{accountData.account_email || 'Loading...'}</span>
              </div>
              <div className="dashboard-item">
                <span className="dashboard-label">Company Name:</span>
                <span className="dashboard-value">{accountData.company_name || 'Loading...'}</span>
              </div>
              {accountData.name_prefix && (
                <div className="dashboard-item">
                  <span className="dashboard-label">Name Prefix:</span>
                  <span className="dashboard-value">{accountData.name_prefix}</span>
                </div>
              )}
              {accountData.account_number && (
                <div className="dashboard-item">
                  <span className="dashboard-label">Account Number:</span>
                  <span className="dashboard-value">{`******${String(accountData.account_number).slice(-6)}`}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Completion Section */}
        {!isTerminalState && stageInfo.isCompleted && !stageInfo.isClosureFlow && (
          <div className="completion-section">
            <div className="success-banner">
              <h2>🎉 Account Setup Complete!</h2>
              <p>Your account has been successfully provisioned and is ready to use.</p>
            </div>
            
            {/* Dashboard URLs Section */}
            <div className="dashboard-urls-section">
              <h3>Access Information</h3>
              <div className="url-cards">
                {adminSignInUrlWithPath && (
                  <div className="url-card">
                    <div className="url-card-header">
                      <span className="url-icon">🏠</span>
                      <h4>Admin Sign-In URL</h4>
                    </div>
                    <a 
                      href={adminSignInHref || adminSignInUrlWithPath} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="url-link"
                    >
                      {adminSignInHref || adminSignInUrlWithPath}
                    </a>
                  </div>
                )}

                {signUpUrl && (
                  <div className="url-card">
                    <div className="url-card-header">
                      <span className="url-icon">📝</span>
                      <h4>Sign-Up URL</h4>
                    </div>
                    <a
                      href={signUpHref || signUpUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="url-link"
                    >
                      {signUpHref || signUpUrl}
                    </a>
                  </div>
                )}
              </div>
              
              {/* Additional Account Details */}
              <div className="account-details-grid">
                {accountData.cloudfront_distribution_id && (
                  <div className="detail-item">
                    <span className="detail-label">CloudFront Distribution ID:</span>
                    <span className="detail-value">{accountData.cloudfront_distribution_id}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Closure Completion Section */}
        {!isTerminalState && stageInfo.isCompleted && stageInfo.isClosureFlow && (
          <div className="completion-section">
            <div className="success-banner closure-success">
              <h2>✅ Account Closure Complete!</h2>
              <p>All resources have been successfully removed and the account has been closed.</p>
            </div>
            
            <div className="closure-summary">
              <h3>Closure Summary</h3>
              <div className="summary-details">
                <div className="summary-item">
                  <span className="summary-icon">✓</span>
                  <span>Dashboard resources removed</span>
                </div>
                <div className="summary-item">
                  <span className="summary-icon">✓</span>
                  <span>Infrastructure resources removed</span>
                </div>
                <div className="summary-item">
                  <span className="summary-icon">✓</span>
                  <span>Account closure completed</span>
                </div>
              </div>
            </div>

            {archivePanel}
          </div>
        )}

        {/* Terminal State Layout (Simplified Dashboard Look) */}
        {isTerminalState && (
          <div className="terminal-state-content">
             {stageInfo.isCompleted && !stageInfo.isClosureFlow && (
               <div className="completion-access-links">
                  <h3>Access Information</h3>
                  <div className="url-cards">
                    {adminSignInUrlWithPath && (
                      <div className="url-card">
                        <div className="url-card-header">
                          <span className="url-icon">🏠</span>
                          <h4>Admin Sign-In URL</h4>
                        </div>
                        <a 
                          href={adminSignInHref || adminSignInUrlWithPath} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="url-link"
                        >
                          {adminSignInHref || adminSignInUrlWithPath}
                        </a>
                      </div>
                    )}

                    {signUpUrl && (
                      <div className="url-card">
                        <div className="url-card-header">
                          <span className="url-icon">📝</span>
                          <h4>Sign-Up URL</h4>
                        </div>
                        <a
                          href={signUpHref || signUpUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="url-link"
                        >
                          {signUpHref || signUpUrl}
                        </a>
                      </div>
                    )}
                  </div>
                  
                  <div className="account-details-grid">
                    {accountData.cloudfront_distribution_id && (
                      <div className="detail-item">
                        <span className="detail-label">CloudFront Distribution ID:</span>
                        <span className="detail-value">{accountData.cloudfront_distribution_id}</span>
                      </div>
                    )}
                  </div>
               </div>
             )}

             {/* Modify Users — visible only on a live COMPLETED deployment */}
             {stageInfo.isCompleted && !stageInfo.isClosureFlow && (
               <div className="modify-users-section">
                 {modifySuccess && (
                   <p className="modify-success-text">{modifySuccess}</p>
                 )}
                 {!modifyOpen ? (
                   <button
                     className="modify-users-button"
                     onClick={() => {
                       setModifyMin(String(accountData.fleet_min_capacity || 1));
                       setModifyMax(String(accountData.fleet_max_capacity || 5));
                       setModifySavePaymentMethod(true);
                       setModifyError(null);
                       setModifySuccess(null);
                       setModifyStep('form');
                       setModifyQuote(null);
                       setModifyStripePromise(null);
                       setModifyOpen(true);
                     }}
                   >
                     Modify Users
                   </button>
                 ) : modifyStep === 'paying' && modifyQuote && modifyStripePromise ? (
                   <div className="modify-users-form">
                     {modifyError && <p className="modify-error-text">{modifyError}</p>}
                     <Elements stripe={modifyStripePromise} options={{ clientSecret: modifyQuote.clientSecret }}>
                       <CapacityPaymentForm
                         quote={modifyQuote}
                         onPaid={handleCapacityPaid}
                         onCancel={() => {
                           setModifyStep('form');
                           setModifyQuote(null);
                           setModifyStripePromise(null);
                           setModifyError(null);
                         }}
                       />
                     </Elements>
                   </div>
                 ) : (
                   <div className="modify-users-form">
                     <h4>Update Fleet Capacity</h4>
                     <div className="modify-field-row">
                       <label className="modify-label">
                         Min concurrent users (1–100)
                         <input
                           type="number"
                           className="modify-input"
                           min={1}
                           max={100}
                           value={modifyMin}
                           onChange={e => setModifyMin(e.target.value)}
                           disabled={modifyLoading}
                         />
                       </label>
                       <label className="modify-label">
                         Max concurrent users (1–100)
                         <input
                           type="number"
                           className="modify-input"
                           min={1}
                           max={100}
                           value={modifyMax}
                           onChange={e => setModifyMax(e.target.value)}
                           disabled={modifyLoading}
                         />
                       </label>
                     </div>
                     <p className="modify-escrow-note">
                       Need more than 100 concurrent users? Please contact support at 800-985-1351.
                     </p>
                     <p className="modify-escrow-note">
                       Increasing the user count requires a one-time service fee for the added users
                       plus an escrow top-up. Reducing users adjusts the ceiling only — service fees
                       are non-refundable.
                     </p>
                     <label className="modify-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 400 }}>
                       <input
                         type="checkbox"
                         checked={modifySavePaymentMethod}
                         onChange={e => setModifySavePaymentMethod(e.target.checked)}
                         disabled={modifyLoading}
                       />
                       Save this card as my default payment method for future charges
                     </label>
                     {modifyError && <p className="modify-error-text">{modifyError}</p>}
                     <div className="modify-action-row">
                       <button
                         className="modify-apply-button"
                         onClick={handleReviewCapacity}
                         disabled={modifyLoading}
                       >
                         {modifyLoading ? 'Loading…' : 'Review Changes'}
                       </button>
                       <button
                         className="modify-cancel-button"
                         onClick={() => { setModifyOpen(false); setModifyError(null); }}
                         disabled={modifyLoading}
                       >
                         Cancel
                       </button>
                     </div>
                   </div>
                 )}
               </div>
             )}

             {/* Update Payment Method — standalone, works anytime (not tied to resizing) */}
             {stageInfo.isCompleted && !stageInfo.isClosureFlow && (
               <div className="modify-users-section">
                 {paymentUpdateSuccess && <p className="modify-success-text">{paymentUpdateSuccess}</p>}
                 {paymentUpdateError && <p className="modify-error-text">{paymentUpdateError}</p>}
                 {!paymentUpdateOpen ? (
                   <button
                     className="modify-users-button"
                     onClick={handleStartPaymentMethodUpdate}
                     disabled={paymentUpdateLoading}
                   >
                     {paymentUpdateLoading ? 'Loading…' : 'Update Payment Method'}
                   </button>
                 ) : (
                   <div className="modify-users-form">
                     <h4>Update Payment Method</h4>
                     {paymentUpdateClientSecret && paymentUpdateStripePromise && (
                       <Elements stripe={paymentUpdateStripePromise} options={{ clientSecret: paymentUpdateClientSecret }}>
                         <UpdatePaymentMethodForm
                           onConfirmed={handlePaymentMethodConfirmed}
                           onCancel={() => {
                             setPaymentUpdateOpen(false);
                             setPaymentUpdateClientSecret(null);
                             setPaymentUpdateStripePromise(null);
                             setPaymentUpdateError(null);
                           }}
                         />
                       </Elements>
                     )}
                   </div>
                 )}
               </div>
             )}

             {stageInfo.isCompleted && stageInfo.isClosureFlow && (
               <>
                 <div className="closure-summary">
                    <h3>Closure Summary</h3>
                    <div className="summary-details">
                      <div className="summary-item">
                        <span className="summary-icon">✓</span>
                        <span>Dashboard resources removed</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-icon">✓</span>
                        <span>Infrastructure resources removed</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-icon">✓</span>
                        <span>Account closure completed</span>
                      </div>
                    </div>
                 </div>
                 {archivePanel}
               </>
             )}

             {stageInfo.isFailed && (
               <div className="failure-summary">
                 <h3>Status: {statusMessage}</h3>
               </div>
             )}
          </div>
        )}

        {/* Onboarding Information Board */}
        {!isTerminalState && !stageInfo.isClosureFlow && (
          <div className="onboarding-section">
            <h3>Onboarding Information</h3>
            <div className="onboarding-content">
              <p className="onboarding-intro">
                Once the deployment reaches the <strong>Completed</strong> state, follow these steps to bring users online.
              </p>
              <ol className="onboarding-steps">
                <li>
                  <strong>Admin Dashboard Login:</strong>{' '}
                  {adminSignInUrl ? (
                    <>
                      Visit <a href={adminSignInHref || adminSignInUrl} target="_blank" rel="noopener noreferrer">the admin dashboard</a> and sign in using the administrator credentials emailed to you during the deployment process. After successfully logging in, you can start managing your users&#39; activities.
                    </>
                  ) : (
                    <span>The admin dashboard link will appear here once the deployment finishes. Visit the admin dashboard and sign in using the administrator credentials emailed to you during the deployment process. After successfully logging in, you can start managing your users&#39; activities.</span>
                  )}
                </li>
                <li>
                  <strong>User Workstation Login:</strong>{' '}
                  {workstationUrl ? (
                    <>
                      Launch and access the workspace from{' '}
                      <a href={workstationHref || workstationUrl} target="_blank" rel="noopener noreferrer">this link</a>,{' '}
                      then sign in using the user credentials provided by your administrator.
                    </>
                  ) : (
                    <span>Launch and access the workspace from this link once it becomes available. Then, sign in using the user credentials provided by your administrator.</span>
                  )}
                </li>
              </ol>
              <p className="onboarding-note">
                If you need further assistance, please consult the FAQ for answers to common questions or contact us at{' '}
                <a href="mailto:support@remotetek.co">support@remotetek.co</a>.
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          {(stageInfo.isFailed || isRejectedStatus(normalizedStatus)) && (
            <button onClick={handleBackToDashboard} className="back-button">
              {backDashboardLabel}
            </button>
          )}
          {!stageInfo.isCompleted && !stageInfo.isFailed && (
            <button onClick={() => { setLoading(true); fetchAccountStatus(); }} className="refresh-button">
              Refresh Status
            </button>
          )}
        </div>

        {!stageInfo.isCompleted && !stageInfo.isFailed && (
          <p className="auto-refresh-note">
            <small>Status updates automatically every 10 seconds</small>
          </p>
        )}
      </div>
    </div>
  );
};

export default AccountStatus;
